import { ComputedRef, computed } from '@vue/composition-api'
import store from '@/store'
import { Cast } from '@/modules/casts/classes'
import { useConfirmation } from '@/modules/kisweui/compositions'
import { useCastStreams } from '@/modules/streams/compositions'
import { SourceStreamCaster, SourceStreamWithCast } from '@/types/streams'
import { WRTCServerType } from '@/types/admin'
import { useCurrentCast } from '@/modules/casts/compositions'
import { useAddLog } from '@/modules/logging/compositions'

type SourceStreamCasterWithCast = SourceStreamCaster & { castId: string }

const casterTypeFilter = (entry: [string, SourceStreamWithCast]): entry is [string, SourceStreamCasterWithCast] => {
  return 'user_id' in entry[1]
}

const useCasterStream = (casterId: ComputedRef<string>, castParam?: ComputedRef<Cast|null>) => {
  const { askConfirmation } = useConfirmation()
  const { castStreams, sourceStreams } = useCastStreams()
  const { currentCast } = useCurrentCast()
  const { addLogNoWait } = useAddLog()

  const cast = computed(() => castParam === undefined ? currentCast.value : castParam.value)

  const casterStream = computed(() => {
    const sourceStreamKey = Object.entries(sourceStreams.value)
      .filter(casterTypeFilter)
      .find(([_key, value]) => value.user_id === casterId.value)?.[0] ?? null
    if (sourceStreamKey === null) return null
    const isJanus = cast.value?.wrtcServerType === WRTCServerType.JANUS
    const castStreamKey = isJanus ? sourceStreamKey : `${ sourceStreamKey }_lowrate`
    if (castStreamKey === undefined) return null
    return castStreams.value[castStreamKey] ?? null
  })

  const republishCaster = async () => {
    await askConfirmation({
      question: 'Are you sure you want this caster stream to restart?',
      action: async () => {
        if (!cast.value?.id) throw new Error('Cannot request republish with invalid cast id')
        addLogNoWait(`Requesting caster republish for ${casterId.value}`, 'info')
        return await store.dispatch.events.requestCasterRepublish({ cast: cast.value?.id, caster: casterId.value })
      }
    })
  }

  return {
    casterStream,
    republishCaster
  }
}

export default useCasterStream
